import { createActions } from 'redux-actions';
import { ofType } from 'redux-observable';
import get from 'lodash/get';
import set from 'lodash/set';
import Cookies from 'js-cookie';
import { message } from 'antd';
import {
  map, mergeMap, catchError,
} from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import * as ACTION_CONSTS from '../constants/actions.constants';
import { ENDPOINTS, RAW_DATA } from '../plannerConfig';
import { showExpiredSessionModal } from '../components/TokenRefresher';

const {
  request,
  receiveData,
  deleteDataSuccess,
  updateDataSuccess,
  addDataSuccess,
} = createActions({
  [ACTION_CONSTS.REQUEST]: [
    () => null,
    (entities, method, meta) => ({ entities, method, ...meta }),
  ],
  [ACTION_CONSTS.RECEIVE_DATA]: [payload => payload, (payload, meta) => meta],
  [ACTION_CONSTS.DELETE_DATA_SUCCESS]: [payload => payload, (payload, meta) => meta],
  [ACTION_CONSTS.UPDATE_DATA_SUCCESS]: [payload => payload, (payload, meta) => meta],
  [ACTION_CONSTS.ADD_DATA_SUCCESS]: [payload => payload, (payload, meta) => meta],
});

let sessionExpired = false;

// Can't use RxJs Ajax due to ops reasons. Use Fetch + RxJs for maximum control on error handling.
const apiCall = (method, { url, ...rest }) => fetch(url, {
  method,
  ...rest, // Data is passed down as text due to null.
}).then(response => Promise.all([response, response.text()]))
  .then(([{ ok, status, statusText }, data]) => {
    if (!ok) {
      const error = { status, statusText, errorMsgFromDjango: data };
      throw error;
    }
    try { // Return json if construction of json is successful.
      const json = JSON.parse(data);
      return json;
    } catch { // Otherwise return plain text.
      return data;
    }
  });

const AJAX_MAPPINGS = {
  GET: requestObj => apiCall('get', requestObj),
  PUT: requestObj => apiCall('put', requestObj),
  POST: requestObj => apiCall('post', requestObj),
  DELETE: requestObj => apiCall('delete', requestObj),
};

const ACTION_MAPPINGS = {
  GET: receiveData,
  DELETE: deleteDataSuccess,
  PUT: updateDataSuccess,
  POST: addDataSuccess,
};

const getActionIntent = (action) => {
  const { entities, endpoints } = action.meta;
  const ajaxMethod = AJAX_MAPPINGS[action.meta.method];
  const useExplicitEndpoints = action.meta.endpoints;

  return {
    ajaxMethod,
    useExplicitEndpoints,
    mapSource: useExplicitEndpoints ? endpoints : entities,
  };
};

const mapEndpointParams = (endpoint, routeParams) => {
  if (typeof endpoint === 'function') {
    return endpoint(routeParams);
  }
  return endpoint;
};

const deriveRequestEndpoints = (action) => {
  const { mapSource, ajaxMethod } = getActionIntent(action);

  const headers = {
    'Content-Type': 'application/json',
    'X-CSRFToken': Cookies.get('djangocsrftoken'),
  };

  const body = action.meta.body
    ? { body: JSON.stringify(action.meta.body) }
    : {};

  return mapSource.map(e => ajaxMethod({
    url: mapEndpointParams(ENDPOINTS[e].endpoint[action.meta.method], action.meta.routeParams),
    headers,
    // GET and DELETE don't have a body as a param, as such need to exclude it.
    ...body,
  }));
};

const mapResponses = (action, responses) => responses.reduce((acc, curr, j) => {
  const currentEntity = action.meta.entities[j];

  let derivedResponse = action.meta.method === 'GET'
    ? responses[j]
    : responses[j].response || responses[j]; // VisualisationData endpoint doesn't have .response.

  if (action.meta.method === 'DELETE' && !derivedResponse) {
    derivedResponse = { pk: action.meta.deletedRecordId };
  }

  const response = ENDPOINTS[currentEntity].higherOrder
    ? set(
      {},
      get(action.meta, ENDPOINTS[currentEntity].higherOrderKeyPath),
      derivedResponse,
    )
    : derivedResponse;

  if (action.meta.callback) {
    action.meta.callback(response);
  }

  return {
    ...acc,
    [action.meta.entities[j]]: response,
  };
}, {});

export const getDataEpic = action$ => action$.pipe(
  ofType(ACTION_CONSTS.REQUEST),
  mergeMap(action => of(
    deriveRequestEndpoints(action),
  ).pipe(
    mergeMap(
      endpoints => forkJoin(endpoints),
    ),
    map(responses => ACTION_MAPPINGS[action.meta.method](
      mapResponses(action, responses),
      { entities: action.meta.entities },
    )),
    catchError((error) => {
      const { status, statusText, errorMsgFromDjango } = error;
      let errorMessage = '';
      try {
        errorMessage = JSON.parse(errorMsgFromDjango).detail;
      } catch {
        errorMessage = errorMsgFromDjango || statusText;
      }

      // With session auth, an unauthenticated request returns 403 with this
      // detail (DRF SessionAuthentication sends no WWW-Authenticate for 401).
      const notAuthenticated =
        status === 401 ||
        (status === 403 &&
          /Authentication credentials were not provided/i.test(errorMessage || ''));

      if (notAuthenticated) {
        if (!sessionExpired) showExpiredSessionModal();
        sessionExpired = true;
      } else if (status === 500 || status === 504) {
        message.error(statusText, 6); // these messages should show for longer (6 seconds).
      } else {
        // If have errors in a callback happens, Antd message the error string.
        // But if you want to find errors for "upload in data page". You come to the wrong place.
        // The upload error is handled in dataPage component.
        message.error(errorMessage);
      }

      if (action.meta.errorCallback) {
        action.meta.errorCallback(error);
      }

      return of({
        type: ACTION_CONSTS.REQUEST_FAILURE,
        meta: action.meta,
        error,
      });
    }),
  )),
);

// Set any Redux store states without endpoint call.
export const noEndPointSet = (entities, payload) => ({
  type: 'NOENDPOINT_SET_DATA',
  payload,
  meta: { entities },
});

export const noEndPointClear = entities => ({
  type: 'NOENDPOINT_CLEAR_DATA',
  meta: { entities },
});

export default {
  get: (entities, meta) => request(entities, 'GET', meta),
  post: (entities, meta) => request(entities, 'POST', meta),
  put: (entities, meta) => request(entities, 'PUT', meta),
  delete: (entities, meta) => request(entities, 'DELETE', meta),
  set: (entities, payload) => noEndPointSet(entities, payload),
  clear: entities => noEndPointClear(entities),
};
